/**
 * policyBuilderState.js — Pure functions for the approval policy builder.
 * No React dependencies. Handles defaults, parsing, serialization,
 * validation, overlap detection, preview simulation, and legacy migration.
 */

let _idCounter = 0;
function clientId() {
  _idCounter += 1;
  return 'tier_' + Date.now().toString(36) + '_' + _idCounter;
}
export function stepId() {
  _idCounter += 1;
  return 'step_' + Date.now().toString(36) + '_' + _idCounter;
}

const ROLE_LABELS = {
  finance_owner: 'Finance Owner',
  ops_owner: 'Ops Owner',
  cfo: 'CFO',
  exec: 'Exec',
};
export { ROLE_LABELS };

export const ROLES = Object.keys(ROLE_LABELS);

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

export function createDefaultTier() {
  return {
    id: clientId(),
    name: '',
    min_amount: null,
    max_amount: null,
    any_amount: true,
    required: { finance_owner: false, ops_owner: false, cfo: false, exec: false },
    context: { near_term_only: false, within_days: 90 },
    chain: [],
  };
}

export function createDefaultPolicy(objectType) {
  return {
    metadata: {
      name: '',
      object_type: objectType || 'budget_period_version',
      budget_type: '',
      priority: 100,
      status: 'enabled',
      currency: 'EUR',
    },
    definition: {
      definition_version: 1,
      tiers: [createDefaultTier()],
    },
  };
}

export function createDefaultStep() {
  return {
    id: stepId(),
    approver_type: 'role',
    role: 'finance_owner',
    user_id: null,
    approval_mode: 'any_of',
    label: ROLE_LABELS.finance_owner,
    allow_duplicate_override: false,
  };
}

// ---------------------------------------------------------------------------
// Parsing / Serialization
// ---------------------------------------------------------------------------

function isTierArray(arr) {
  return Array.isArray(arr) && arr.every(
    (t) => t && typeof t === 'object' && Array.isArray(t.chain)
  );
}

export function parsePolicyDefinition(policyJson) {
  if (!policyJson || typeof policyJson !== 'object') {
    return { ok: false, definition: null, metadata: null, isLegacy: true };
  }
  if (policyJson.definition_version !== 1 || !isTierArray(policyJson.tiers)) {
    return { ok: false, definition: null, metadata: null, isLegacy: true };
  }
  const tiers = policyJson.tiers.map((t) => ({
    id: t.id || clientId(),
    name: t.name || '',
    min_amount: t.min_amount ?? null,
    max_amount: t.max_amount ?? null,
    any_amount: !!t.any_amount,
    required: {
      finance_owner: !!t.required?.finance_owner,
      ops_owner: !!t.required?.ops_owner,
      cfo: !!t.required?.cfo,
      exec: !!t.required?.exec,
    },
    context: {
      near_term_only: !!t.context?.near_term_only,
      within_days: t.context?.within_days ?? 90,
    },
    chain: (t.chain || []).map((s) => ({
      id: s.id || stepId(),
      approver_type: s.approver_type || 'role',
      role: s.role || null,
      user_id: s.user_id || null,
      approval_mode: s.approval_mode || 'any_of',
      label: s.label || '',
      allow_duplicate_override: !!s.allow_duplicate_override,
    })),
  }));
  const extraMeta = {
    budget_type: policyJson.budget_type || '',
    priority: typeof policyJson.priority === 'number' ? policyJson.priority : 100,
    currency: policyJson.currency || 'EUR',
  };
  return {
    ok: true,
    definition: { definition_version: 1, tiers },
    metadata: extraMeta,
    isLegacy: false,
  };
}

export function serializeToPayload(metadata, definition) {
  const currency = metadata.currency ?? 'EUR';
  const tiers = definition.tiers.map((t) => ({
    id: t.id,
    name: t.name || generateTierName(t, currency),
    min_amount: t.min_amount,
    max_amount: t.max_amount,
    any_amount: t.any_amount,
    required: { ...t.required },
    context: { ...t.context },
    chain: t.chain.map((s) => ({
      id: s.id,
      approver_type: s.approver_type,
      role: s.role,
      user_id: s.user_id,
      approval_mode: s.approval_mode,
      label: s.label,
      allow_duplicate_override: s.allow_duplicate_override,
    })),
  }));
  return {
    name: metadata.name,
    object_type: metadata.object_type,
    is_enabled: true,
    policy_json: {
      definition_version: 1,
      budget_type: metadata.budget_type || undefined,
      priority: metadata.priority ?? 100,
      currency,
      tiers,
    },
  };
}

// ---------------------------------------------------------------------------
// Non-destructive merge for save (preserves unknown fields)
// ---------------------------------------------------------------------------

const KNOWN_DEFINITION_KEYS = new Set([
  'definition_version', 'budget_type', 'priority', 'currency', 'tiers',
]);

/**
 * Merge builder-serialized definition onto the original raw policy_json,
 * preserving any unknown top-level keys the backend may have stored.
 * Tiers are fully builder-managed and replaced wholesale.
 *
 * @param {object|null} originalRaw - deep clone of the fetched policy_json
 * @param {object} builderSerialized - the policy_json produced by serializeToPayload
 * @returns {object} merged policy_json safe for PUT
 */
export function mergeDefinitionForSave(originalRaw, builderSerialized) {
  if (!originalRaw || typeof originalRaw !== 'object') {
    return builderSerialized;
  }
  const merged = {};
  for (const key of Object.keys(originalRaw)) {
    if (!KNOWN_DEFINITION_KEYS.has(key)) {
      merged[key] = originalRaw[key];
    }
  }
  for (const key of Object.keys(builderSerialized)) {
    merged[key] = builderSerialized[key];
  }
  return merged;
}

// ---------------------------------------------------------------------------
// Auto-name generation
// ---------------------------------------------------------------------------

function fmtAmount(n, currency = 'EUR') {
  if (n == null) return '';
  try {
    return new Intl.NumberFormat('nl-NL', {
      style: 'currency',
      currency: currency || 'EUR',
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return String(n);
  }
}

export function generateTierName(tier, currency = 'EUR') {
  if (tier.any_amount) return 'Any amount';
  const hasMin = tier.min_amount != null;
  const hasMax = tier.max_amount != null;
  const fmt = (n) => fmtAmount(n, currency);
  if (hasMin && hasMax) return `${fmt(tier.min_amount)} – ${fmt(tier.max_amount)}`;
  if (hasMin) return `Above ${fmt(tier.min_amount)}`;
  if (hasMax) return `Up to ${fmt(tier.max_amount)}`;
  return 'Any amount';
}

/**
 * Sort tiers by starting amount: tiers with no min_amount (any_amount or null) first,
 * then by min_amount ascending.
 * @param {object[]} tiers - Tier objects
 * @returns {object[]} New sorted array (does not mutate input)
 */
export function sortTiersByMinAmount(tiers) {
  if (!tiers || tiers.length === 0) return tiers || [];
  return [...tiers].sort((a, b) => {
    const aNoStart = a.any_amount || a.min_amount == null;
    const bNoStart = b.any_amount || b.min_amount == null;
    if (aNoStart && !bNoStart) return -1;
    if (!aNoStart && bNoStart) return 1;
    if (aNoStart && bNoStart) return 0;
    return (a.min_amount ?? 0) - (b.min_amount ?? 0);
  });
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

export function detectTierOverlaps(tiers) {
  const overlaps = [];
  for (let i = 0; i < tiers.length; i++) {
    for (let j = i + 1; j < tiers.length; j++) {
      const a = tiers[i];
      const b = tiers[j];
      if (a.any_amount || b.any_amount) {
        if (tiers.length > 1) overlaps.push([a.id, b.id]);
        continue;
      }
      const aMin = a.min_amount ?? -Infinity;
      const aMax = a.max_amount ?? Infinity;
      const bMin = b.min_amount ?? -Infinity;
      const bMax = b.max_amount ?? Infinity;
      if (aMin < bMax && bMin < aMax) {
        overlaps.push([a.id, b.id]);
      }
    }
  }
  return overlaps;
}

export function validatePolicy(metadata, definition, isCreate) {
  const errors = [];
  const warnings = [];

  if (!metadata.name?.trim()) {
    errors.push({ field: 'name', message: 'Policy name is required.' });
  }
  if (isCreate && !metadata.object_type?.trim()) {
    errors.push({ field: 'object_type', message: 'Object type is required.' });
  }

  const { tiers } = definition;
  if (!tiers || tiers.length === 0) {
    errors.push({ field: 'tiers', message: 'At least one tier is required.' });
    return { valid: errors.length === 0, errors, warnings };
  }

  for (const tier of tiers) {
    if (tier.min_amount != null && tier.max_amount != null && tier.min_amount >= tier.max_amount) {
      errors.push({ field: 'tier', tierId: tier.id, message: `Tier "${tier.name || generateTierName(tier)}": min must be less than max.` });
    }
    const hasRequired = ROLES.some((r) => tier.required[r]);
    if (hasRequired && tier.chain.length === 0) {
      errors.push({ field: 'chain', tierId: tier.id, message: `Tier "${tier.name || generateTierName(tier)}": required approvals set but chain is empty.` });
    }
    for (const role of ROLES) {
      if (tier.required[role]) {
        const hasMatch = tier.chain.some((s) => s.role === role);
        if (!hasMatch) {
          errors.push({ field: 'chain', tierId: tier.id, message: `Tier "${tier.name || generateTierName(tier)}": ${ROLE_LABELS[role]} is required but not in chain.` });
        }
      }
    }
    const roleCounts = {};
    for (const step of tier.chain) {
      if (step.approver_type === 'role' && step.role) {
        roleCounts[step.role] = (roleCounts[step.role] || 0) + 1;
      }
    }
    for (const [role, count] of Object.entries(roleCounts)) {
      if (count > 1) {
        const hasDupOverride = tier.chain
          .filter((s) => s.role === role)
          .every((s) => s.allow_duplicate_override);
        if (!hasDupOverride) {
          errors.push({ field: 'chain', tierId: tier.id, message: `Tier "${tier.name || generateTierName(tier)}": duplicate ${ROLE_LABELS[role]} in chain.` });
        }
      }
    }
    const execSteps = tier.chain
      .map((s, idx) => ({ ...s, _idx: idx }))
      .filter((s) => s.role === 'exec');
    if (execSteps.length > 0) {
      const lastIdx = tier.chain.length - 1;
      if (execSteps.some((s) => s._idx !== lastIdx)) {
        warnings.push({ field: 'chain', tierId: tier.id, message: `Tier "${tier.name || generateTierName(tier)}": Exec step is not last in chain.` });
      }
    }
  }

  const overlaps = detectTierOverlaps(tiers);
  if (overlaps.length > 0) {
    errors.push({ field: 'tiers', message: 'Tier amount ranges overlap.' });
  }

  const derivedSummary = {
    tiers: tiers.map((tier) => {
      const rolesInChain = [...new Set(
        tier.chain.filter((s) => s.approver_type === 'role' && s.role).map((s) => s.role)
      )];
      const rolesRequired = ROLES.filter((r) => tier.required[r]);
      const missingRoles = rolesRequired.filter((r) => !rolesInChain.includes(r));
      return {
        tierId: tier.id,
        tierName: tier.name || generateTierName(tier),
        rolesInChain,
        rolesRequired,
        missingRoles,
        isCompliant: missingRoles.length === 0,
      };
    }),
  };

  return { valid: errors.length === 0, errors, warnings, derivedSummary };
}

// ---------------------------------------------------------------------------
// Preview simulation
// ---------------------------------------------------------------------------

export function simulatePreview({ amountDelta, requiredFlags, nearTerm, withinDays }, tiers) {
  const emptyResult = {
    matchedTier: null, approvalRequired: false, chainSummary: '', message: '',
    matchedConditions: null, enforcedRoles: [],
  };

  if (!tiers || tiers.length === 0) {
    return { ...emptyResult, message: 'No tiers defined.' };
  }

  let matched = null;
  let matchedConditions = null;
  const amt = amountDelta ?? 0;

  for (const tier of tiers) {
    if (tier.any_amount) {
      matched = tier;
      matchedConditions = { amountInRange: true, contextMatch: true };
      break;
    }
    const min = tier.min_amount ?? -Infinity;
    const max = tier.max_amount ?? Infinity;
    const amountInRange = amt >= min && amt < max;
    if (amountInRange) {
      const contextMatch = !tier.context?.near_term_only || nearTerm;
      if (!contextMatch) continue;
      matched = tier;
      matchedConditions = { amountInRange: true, contextMatch };
      break;
    }
  }

  if (!matched) {
    return { ...emptyResult, message: 'No tier matched. No approval required.' };
  }

  const tierName = matched.name || generateTierName(matched);

  const enforcedRoles = ROLES.map((role) => {
    const requiredByTier = !!matched.required[role];
    const stepIndex = matched.chain.findIndex((s) => s.approver_type === 'role' && s.role === role);
    return {
      role,
      label: ROLE_LABELS[role],
      requiredByTier,
      inChainAtStep: stepIndex >= 0 ? stepIndex : null,
    };
  }).filter((r) => r.requiredByTier || r.inChainAtStep !== null);

  const hasRequired = requiredFlags
    ? ROLES.some((r) => requiredFlags[r] && matched.required[r])
    : ROLES.some((r) => matched.required[r]);

  if (!hasRequired && matched.chain.length === 0) {
    return {
      matchedTier: tierName,
      approvalRequired: false,
      chainSummary: '',
      message: `Tier "${tierName}" matched. No approval required.`,
      matchedConditions,
      enforcedRoles,
    };
  }

  const chainLabels = matched.chain.map((s) => s.label || (s.role ? ROLE_LABELS[s.role] : 'User'));
  const chainSummary = chainLabels.join(' → ');
  return {
    matchedTier: tierName,
    approvalRequired: true,
    chainSummary,
    message: `Tier "${tierName}" matched. Approvals required in order: ${chainSummary}`,
    matchedConditions,
    enforcedRoles,
  };
}

// ---------------------------------------------------------------------------
// Legacy migration
// ---------------------------------------------------------------------------

export function tryMigrateLegacy(policyJson) {
  if (!policyJson || typeof policyJson !== 'object') {
    return { ok: false, definition: null };
  }
  if (Array.isArray(policyJson.steps)) {
    const chain = policyJson.steps.map((s, idx) => ({
      id: stepId(),
      approver_type: s.approver_type || 'role',
      role: s.role || s.approver_role || null,
      user_id: s.user_id || s.approver_user_id || null,
      approval_mode: s.approval_mode || 'any_of',
      label: s.label || (s.role ? (ROLE_LABELS[s.role] || s.role) : `Step ${idx + 1}`),
      allow_duplicate_override: false,
    }));
    const tier = createDefaultTier();
    tier.chain = chain;
    return {
      ok: true,
      definition: { definition_version: 1, tiers: [tier] },
    };
  }
  return { ok: false, definition: null };
}
